// src/pages/ProviderTaxStats.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../api/axios";

const taxTypes = [
    { key: "road_tax", label: "Road Tax" },
    { key: "three_month_tax", label: "Three Month Tax" },
    { key: "aitp", label: "AITP Authorisation" },
];

const STATUS_STYLES = {
    "tax-checking": "bg-amber-50 text-amber-700 ring-amber-600/20",
    "pending-at-payment-of-checking-fee": "bg-orange-50 text-orange-700 ring-orange-600/20",
    refunded: "bg-sky-50 text-sky-700 ring-sky-600/20",
    cancelled: "bg-rose-50 text-rose-700 ring-rose-600/20",
    completed: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    final_payment_pending: "bg-violet-50 text-violet-700 ring-violet-600/20",
    final_payment_done: "bg-teal-50 text-teal-700 ring-teal-600/20",
};

const StatusBadge = ({ status }) => (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset whitespace-nowrap ${STATUS_STYLES[status] || "bg-gray-50 text-gray-600 ring-gray-500/20"}`}>
        {status?.replace(/-|_/g, " ")}
    </span>
);

const Spinner = ({ className = "h-5 w-5" }) => (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
);

const StatCard = ({ label, value, prefix = "" }) => (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="text-xs font-medium text-gray-500">{label}</div>
        <div className="mt-1 text-xl font-semibold text-gray-900">{prefix}{value}</div>
    </div>
);

const ProviderTaxStats = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const taxType = searchParams.get("tax_type") || "three_month_tax";
    const from = searchParams.get("from") || "";
    const to = searchParams.get("to") || "";

    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [data, setData] = useState(null);

    const updateParams = (updates) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([k, v]) => {
            if (v === "" || v === null || v === undefined) next.delete(k);
            else next.set(k, v);
        });
        setSearchParams(next);
    };

    const fetchStats = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/border-tax/three/provider/stats`, {
                params: { tax_type: taxType, from, to },
            });
            setData(data);
        } catch (error) {
            Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.message || "Unable to fetch stats." });
        } finally {
            setLoading(false);
        }
    }, [taxType, from, to]);

    const exportExcel = async () => {
        setExporting(true);
        try {
            const res = await api.get(`/admin/border-tax/three/provider/stats/export`, {
                params: { tax_type: taxType, from, to },
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `provider-tax-report-${Date.now()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            Swal.fire({ icon: "error", title: "Export failed", text: error.response?.data?.message || "Something went wrong." });
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => { fetchStats(); }, [fetchStats]);

    const overall = data?.overall || {};
    const breakdown = data?.status_breakdown || [];

    return (
        <div className="min-h-screen bg-gray-50/50 p-3 sm:p-6">
            <div className="mx-auto max-w-7xl">
                <div className="mb-5 flex flex-col gap-1">
                    <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">Tax Stats</h1>
                    <p className="text-sm text-gray-500">Overview and status-wise breakdown for {data?.provider?.name || "provider"}.</p>
                </div>

                {/* filter bar */}
                <div className="mb-4 rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                        <select
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            value={taxType}
                            onChange={(e) => updateParams({ tax_type: e.target.value })}
                        >
                            {taxTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>

                        <input
                            type="date"
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            value={from}
                            onChange={(e) => updateParams({ from: e.target.value })}
                        />
                        <input
                            type="date"
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            value={to}
                            onChange={(e) => updateParams({ to: e.target.value })}
                        />

                        <button
                            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
                            disabled={exporting}
                            onClick={exportExcel}
                        >
                            {exporting && <Spinner className="h-4 w-4" />}
                            {exporting ? "Exporting..." : "Export Excel"}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-400 shadow-sm">
                        <Spinner className="mx-auto h-6 w-6" />
                    </div>
                ) : (
                    <>
                        {/* overall cards */}
                        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                            <StatCard label="Total Requests" value={overall.total_requests || 0} />
                            <StatCard label="Checking Done" value={overall.checking_done || 0} />
                            <StatCard label="Checking Fee Collected" value={overall.checking_fee_collected || 0} prefix="₹" />
                            <StatCard label="Service Fee Collected" value={overall.service_fee_collected || 0} prefix="₹" />
                            <StatCard label="Final Tax Collected" value={overall.final_tax_collected || 0} prefix="₹" />
                            <StatCard label="Total Payable" value={overall.total_payable || 0} prefix="₹" />
                        </div>

                        {/* status breakdown table */}
                        <div className="hidden overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm md:block">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            {["Status", "Count", "Checking Fee", "Service Fee", "Final Tax", "Total Payable"].map((h) => (
                                                <th key={h} className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {breakdown.map((b) => (
                                            <tr key={b.status} className="transition hover:bg-gray-50">
                                                <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                                                <td className="px-4 py-3 font-medium text-gray-900">{b.count}</td>
                                                <td className="px-4 py-3 text-gray-600">₹{b.checking_fee_collected}</td>
                                                <td className="px-4 py-3 text-gray-600">₹{b.service_fee_collected}</td>
                                                <td className="px-4 py-3 text-gray-600">₹{b.final_tax_collected}</td>
                                                <td className="px-4 py-3 text-gray-600">₹{b.total_payable}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* mobile cards */}
                        <div className="space-y-3 md:hidden">
                            {breakdown.map((b) => (
                                <div key={b.status} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                    <div className="mb-2 flex items-center justify-between">
                                        <StatusBadge status={b.status} />
                                        <span className="text-sm font-semibold text-gray-900">{b.count}</span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
                                        <div><span className="text-gray-400">Check Fee:</span> ₹{b.checking_fee_collected}</div>
                                        <div><span className="text-gray-400">Fee:</span> ₹{b.service_fee_collected}</div>
                                        <div><span className="text-gray-400">Tax:</span> ₹{b.final_tax_collected}</div>
                                        <div><span className="text-gray-400">Payable:</span> ₹{b.total_payable}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ProviderTaxStats;